import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ClassIcon from '../../components/icons/ClassIcon';
import SettingIcon from '../../components/icons/SettingIcon';
import BellIcon from '../../components/icons/BellIcon';
import CheckCircleIcon from '../../components/icons/CheckCircleIcon';
import TimerIcon from '../../components/icons/TimerIcon';
import ShieldIcon from '../../components/icons/HideIcon';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const adminFeatures = [
    {
      title: '학교 관리',
      description: '등록된 학교 정보를 관리하고 새로운 학교를 추가합니다.',
      icon: <ClassIcon width={16} height={12} color="white" />,
      path: '/admin/school-management'
    },
    {
      title: '교사 승인 관리',
      description: '교사 회원가입 요청을 승인/거절합니다.',
      icon: <CheckCircleIcon width={16} height={16} color="white" />,
      path: '/admin/teacher-approval'
    },
    {
      title: '학급 담임 교사 관리',
      description: '학교별로 담임 연결을 확인하고, 학년도 변경 후 남은 담임 정보를 해제합니다.',
      icon: <ClassIcon width={16} height={12} color="white" />,
      path: '/admin/homeroom-teachers'
    },
    {
      title: '시스템 설정',
      description: '시스템 전반적인 설정을 관리합니다.',
      icon: <SettingIcon width={16} height={16} color="white" />,
      path: '/admin/settings'
    },
    {
      title: '사용 통계',
      description: '시스템 사용 현황과 통계를 확인합니다.',
      icon: <TimerIcon width={16} height={16} color="white" />,
      path: '/admin/statistics'
    },
    {
      title: '보안 관리',
      description: '사용자 권한과 보안 설정을 관리합니다.',
      icon: <ShieldIcon width={16} height={16} color="white" />,
      path: '/admin/security'
    },
    {
      title: '계정 등급',
      description: '교사 계정의 무료, 학급, 학교 등급을 확정합니다.',
      icon: <SettingIcon width={16} height={16} color="white" />,
      path: '/admin/teacher-plans'
    },
    {
      title: '알림 관리',
      description: '시스템 알림과 공지사항을 관리합니다.',
      icon: <BellIcon width={16} height={16} fill="white" />,
      path: '/admin/notifications'
    }
  ];

  const recentActivities = [
    {
      title: '교사 승인 완료',
      description: '2024년 1월 14일 - 김교사님의 회원가입이 승인되었습니다.',
      icon: <CheckCircleIcon width={16} height={16} color="var(--Color-fill-brand-color, #FF6F0F)" />
    },
    {
      title: '시스템 업데이트',
      description: '2024년 1월 13일 - 시스템이 최신 버전으로 업데이트되었습니다.',
      icon: <BellIcon width={16} height={16} fill="var(--Color-fill-brand-color, #FF6F0F)" />
    }
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>
          <div
            style={{
              fontSize: '28px',
              fontWeight: 600,
              lineHeight: '42px',
              color: 'var(--Color-text-strong, #0D0D0D)',
              marginBottom: '8px'
            }}
          >
            관리자 대시보드
          </div>
          <div
            style={{
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              color: 'var(--Color-text-normal, #161616)'
            }}
          >
            시스템 전반적인 관리를 위한 기능들을 제공합니다.
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px'
          }}
        >
          {adminFeatures.filter((feature) => feature.path !== '/admin/teacher-plans' || currentUser?.role === 'superAdmin').map((feature) => (
            <Card
              key={feature.path}
              padding="18px 20px"
              gap="14px"
              style={{
                height: '100%',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 1px 2px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05)), 0 0 5px 0 var(--color-alpha-shadow1, rgba(0, 0, 0, 0.05))';
              }}
              onClick={() => navigate(feature.path)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {feature.icon}
                </div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    color: 'var(--Color-text-strong, #0D0D0D)'
                  }}
                >
                  {feature.title}
                </div>
              </div>

              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: 'var(--Color-text-normal, #161616)'
                }}
              >
                {feature.description}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outline"
                  onClick={(e) => {
                    e?.stopPropagation();
                    navigate(feature.path);
                  }}
                >
                  관리하기
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <Card padding="20px" gap="16px">
          <div
            style={{
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '27px',
              color: 'var(--Color-text-strong, #0D0D0D)'
            }}
          >
            최근 활동
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {recentActivities.map((activity, index) => (
              <div
                key={activity.title}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px 0',
                  borderBottom: index === recentActivities.length - 1
                    ? 'none'
                    : '1px solid var(--Color-line-normal, #F0F0F0)'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--Color-fill-hover, #F5F5F5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {activity.icon}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '21px',
                      color: 'var(--Color-text-strong, #0D0D0D)'
                    }}
                  >
                    {activity.title}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '18px',
                      color: 'var(--Color-text-normal, #161616)'
                    }}
                  >
                    {activity.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage; 